import type { UserTrade } from '@/lib/api/speed';

export interface TriggerLevels {
    takeProfitPrice: number | null;
    stopLossPrice: number | null;
}

/** A trigger price as stored on the trade: a decimal string, or absent = no leg. */
function triggerPrice(raw: string | null | undefined): number | null {
    if (raw === null || raw === undefined || raw.trim() === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * The TP/SL levels the chart should draw.
 *
 * An open trade record carries what Hyperliquid is actually resting (the same
 * `takeProfit` / `stopLoss` the trigger editor shows, each backed by a trigger
 * oid), so once one exists it is the whole truth — including a leg the user
 * removed, which has to disappear from the chart rather than fall back to the
 * preset that once armed it.
 *
 * `preset` is `entry × (1 ± armed ROI)` recomputed from the rail's current
 * preset. It stands in only where there is no trade to read: demo mode, and the
 * window between the click and the fill. It is NOT a substitute afterwards — it
 * drifts from the resting order whenever the trigger is edited, the preset is
 * changed with a position open, or the backend rounds to Hyperliquid's tick and
 * significant-figure rules (this user's TP rested at 2829.7 where the preset
 * arithmetic said 2830.02).
 */
export function resolveTriggerLevels(
    trade: Pick<UserTrade, 'takeProfit' | 'stopLoss'> | null,
    preset: TriggerLevels,
): TriggerLevels {
    if (!trade) return preset;

    return {
        takeProfitPrice: triggerPrice(trade.takeProfit),
        stopLossPrice: triggerPrice(trade.stopLoss),
    };
}
